import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';

const PlanViewer = ({ fileUrl, fileType, fileName }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [retryCount, setRetryCount] = useState(0);
  const MAX_RETRIES = 3;

  // Load image with authentication and retry logic
  const loadFile = useCallback(async (attempt = 0) => {
    if (!fileUrl) {
      setLoading(false);
      setError(true);
      setErrorMessage('URL du plan non disponible');
      return;
    }

    setLoading(true);
    setError(false);
    setErrorMessage('');
    
    try {
      const token = localStorage.getItem('access_token');
      
      if (!token) {
        throw new Error('Token d\'authentification manquant');
      }

      const response = await axios.get(fileUrl, {
        responseType: 'blob',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        timeout: 60000, // 60 second timeout for large images
        onDownloadProgress: (progressEvent) => {
          // Optional: could add progress indicator
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Téléchargement: ${percentCompleted}%`);
        }
      });

      // Validate response
      if (!response.data || response.data.size === 0) {
        throw new Error('Fichier vide reçu');
      }

      // Validate it's an image
      const contentType = response.headers['content-type'] || response.data.type;
      if (contentType && !contentType.startsWith('image/') && !contentType.includes('octet-stream')) {
        throw new Error('Le fichier n\'est pas une image valide');
      }

      // Create blob URL
      const url = URL.createObjectURL(response.data);
      
      // Pre-load the image to ensure it's valid
      await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          console.log(`Image chargée: ${img.width}x${img.height}`);
          resolve();
        };
        img.onerror = () => {
          URL.revokeObjectURL(url);
          reject(new Error('Impossible de décoder l\'image'));
        };
        img.src = url;
      });

      setBlobUrl(url);
      setLoading(false);
      setRetryCount(0);
      console.log('Plan chargé avec succès');

    } catch (err) {
      console.error(`Erreur chargement plan (tentative ${attempt + 1}):`, err);
      
      // Determine error message
      let message = 'Erreur lors du chargement du plan';
      if (err.response?.status === 401) {
        message = 'Session expirée. Veuillez vous reconnecter.';
      } else if (err.response?.status === 404) {
        message = 'Plan non trouvé sur le serveur';
      } else if (err.code === 'ECONNABORTED' || err.message.includes('timeout')) {
        message = 'Délai d\'attente dépassé. L\'image est peut-être trop volumineuse.';
      } else if (err.message) {
        message = err.message;
      }

      // Retry logic
      if (attempt < MAX_RETRIES - 1 && !err.response?.status) {
        console.log(`Nouvelle tentative dans 2 secondes...`);
        setTimeout(() => {
          setRetryCount(attempt + 1);
          loadFile(attempt + 1);
        }, 2000);
        return;
      }

      setError(true);
      setErrorMessage(message);
      setLoading(false);
    }
  }, [fileUrl]);

  // Initial load
  useEffect(() => {
    let mounted = true;
    
    if (fileUrl && mounted) {
      loadFile(0);
    }

    // Cleanup
    return () => {
      mounted = false;
      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fileUrl, loadFile]);

  // Manual retry handler
  const handleRetry = () => {
    if (blobUrl) {
      URL.revokeObjectURL(blobUrl);
      setBlobUrl(null);
    }
    loadFile(0);
  };

  // Loading state
  if (loading) {
    return (
      <div style={{ 
        width: '100%', 
        height: '600px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        backgroundColor: '#0F1419' 
      }}>
        <div style={{ textAlign: 'center', color: '#8A92B2' }}>
          <div 
            className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-t-2" 
            style={{ borderColor: '#00C2FF' }}
          ></div>
          <p className="mt-4">
            {retryCount > 0 
              ? `Nouvelle tentative (${retryCount}/${MAX_RETRIES})...` 
              : 'Chargement du plan...'}
          </p>
        </div>
      </div>
    );
  }

  // Error state with retry button
  if (error || !blobUrl) {
    return (
      <div style={{ 
        width: '100%', 
        height: '600px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        backgroundColor: '#0F1419' 
      }}>
        <div style={{ textAlign: 'center', maxWidth: '400px', padding: '20px' }}>
          <p style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</p>
          <p style={{ fontSize: '16px', color: '#FF8A8A', marginBottom: '8px' }}>
            Erreur lors du chargement du plan
          </p>
          <p style={{ fontSize: '14px', color: '#8A92B2', marginBottom: '24px' }}>
            {errorMessage || 'Une erreur inattendue s\'est produite'}
          </p>
          <button
            onClick={handleRetry}
            style={{
              padding: '12px 24px',
              backgroundColor: '#00C2FF',
              color: '#1A1E2E',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#00A8E0';
              e.currentTarget.style.transform = 'scale(1.02)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#00C2FF';
              e.currentTarget.style.transform = 'scale(1)';
            }}
          >
            🔄 Réessayer
          </button>
        </div>
      </div>
    );
  }

  // Success state - Image viewer with zoom and pan
  return (
    <div style={{ width: '100%', height: '600px', position: 'relative', backgroundColor: '#0F1419' }}>
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={4}
        wheel={{ step: 0.1 }}
        doubleClick={{ disabled: false }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {/* Controls */}
            <div style={{
              position: 'absolute',
              top: '16px',
              right: '16px',
              zIndex: 10,
              display: 'flex',
              gap: '8px',
              backgroundColor: 'rgba(26, 30, 46, 0.95)',
              padding: '8px',
              borderRadius: '8px',
              border: '1px solid #3A4359'
            }}>
              <button
                onClick={() => zoomIn()}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                title="Zoomer"
              >
                +
              </button>
              <button
                onClick={() => zoomOut()}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '18px',
                  fontWeight: 'bold'
                }}
                title="Dézoomer"
              >
                −
              </button>
              <button
                onClick={() => resetTransform()}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
                title="Réinitialiser"
              >
                ⟲
              </button>
              <button
                onClick={handleRetry}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
                title="Recharger le plan"
              >
                🔄
              </button>
              <button
                onClick={() => {
                  // Open image in new tab
                  const newWindow = window.open('', '_blank');
                  if (newWindow) {
                    newWindow.document.write(`
                      <!DOCTYPE html>
                      <html>
                        <head>
                          <title>${fileName || 'Plan du cimetière'}</title>
                          <style>
                            body {
                              margin: 0;
                              padding: 0;
                              background-color: #1A1E2E;
                              display: flex;
                              justify-content: center;
                              align-items: center;
                              min-height: 100vh;
                            }
                            img {
                              max-width: 100%;
                              max-height: 100vh;
                              object-fit: contain;
                            }
                          </style>
                        </head>
                        <body>
                          <img src="${blobUrl}" alt="${fileName || 'Plan du cimetière'}" />
                        </body>
                      </html>
                    `);
                    newWindow.document.close();
                  }
                }}
                style={{
                  padding: '8px 12px',
                  backgroundColor: 'rgba(0, 194, 255, 0.15)',
                  color: '#00C2FF',
                  border: '1px solid rgba(0, 194, 255, 0.3)',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
                title="Ouvrir en grand"
              >
                👁️ Voir
              </button>
            </div>

            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <img
                src={blobUrl}
                alt={fileName || 'Plan du cimetière'}
                style={{
                  maxWidth: '100%',
                  maxHeight: '100%',
                  objectFit: 'contain'
                }}
                onError={(e) => {
                  console.error('Erreur de rendu de l\'image');
                  setError(true);
                  setErrorMessage('Impossible d\'afficher l\'image');
                }}
              />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
};

export default PlanViewer;
